import { useCallback, useEffect, useRef, useState } from "react";
import { shortAddress } from "../lib/chain";
import { defaultAvatar } from "../lib/avatar";
import {
  fetchMessages,
  fetchProfiles,
  sendMessage,
  type ChatMessage,
  type Profile,
} from "../lib/social";

type Props = {
  address?: string;
  isConnected: boolean;
  onConnect: () => void;
  onEditProfile: () => void;
  profiles: Record<string, Profile>;
  onProfilesLoaded: (p: Record<string, Profile>) => void;
};

function timeAgo(ts: number) {
  const s = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export function ChatBox({
  address,
  isConnected,
  onConnect,
  onEditProfile,
  profiles,
  onProfilesLoaded,
}: Props) {
  // starts minimized so it never covers the betting panel
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const stickBottom = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const [msgs, profs] = await Promise.all([fetchMessages(), fetchProfiles()]);
      setMessages(msgs);
      onProfilesLoaded(profs);
      setError(null);
    } catch {
      setError("chat backend unreachable");
    }
  }, [onProfilesLoaded]);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 4000);
    return () => clearInterval(t);
  }, [refresh]);

  useEffect(() => {
    if (stickBottom.current && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  // While the rail is open the page shifts over so nothing sits underneath it.
  useEffect(() => {
    document.body.classList.toggle("chat-rail-open", open && expanded);
    return () => document.body.classList.remove("chat-rail-open");
  }, [open, expanded]);

  // 10s between messages so one person can't flood the box.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submitMessage = async () => {
    if (!address || !draft.trim() || sending || cooldown > 0) return;
    setSending(true);
    try {
      await sendMessage(address, draft);
      setDraft("");
      setCooldown(10);
      stickBottom.current = true;
      void refresh();
    } catch {
      setError("message failed to send");
    } finally {
      setSending(false);
    }
  };

  const nameFor = (addr: string) => profiles[addr.toLowerCase()]?.username || shortAddress(addr);
  const avatarFor = (addr: string) =>
    profiles[addr.toLowerCase()]?.avatarUrl || defaultAvatar(addr);

  if (!open) {
    return (
      <button className="chat-fab" onClick={() => setOpen(true)}>
        CHAT
      </button>
    );
  }

  return (
    <aside className={`chat ${expanded ? "chat-expanded" : ""}`}>
      <header className="chat-head">
        <span className="chat-title">TROLLBOX</span>
        <div className="chat-head-btns">
          {isConnected && (
            <button className="chat-mini-btn" onClick={onEditProfile} title="Edit profile">
              PROFILE
            </button>
          )}
          <button
            className="chat-mini-btn"
            onClick={() => setExpanded((e) => !e)}
            title={expanded ? "Shrink" : "Expand to full height"}
          >
            {expanded ? "⤡" : "⤢"}
          </button>
          <button className="chat-mini-btn" onClick={() => setOpen(false)} title="Minimize">
            —
          </button>
        </div>
      </header>

      <div
        className="chat-list"
        ref={listRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          stickBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
        }}
      >
        {messages.length === 0 ? (
          <p className="chat-empty">no messages yet — say something</p>
        ) : (
          messages.map((m) => {
            const p = profiles[m.address.toLowerCase()];
            return (
              <div className="chat-msg" key={m.id}>
                <img className="chat-avatar" src={avatarFor(m.address)} alt="" />
                <div className="chat-msg-body">
                  <div className="chat-msg-meta">
                    <span className="chat-name" title={p?.bio || m.address}>
                      {nameFor(m.address)}
                    </span>
                    <span className="chat-time">{timeAgo(m.createdAt)}</span>
                  </div>
                  <p className="chat-text">{m.text}</p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {error && <p className="chat-error">{error}</p>}

      {isConnected ? (
        <div className="chat-input-row">
          <input
            className="chat-input"
            placeholder="say something…"
            value={draft}
            maxLength={280}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void submitMessage();
            }}
          />
          <button
            className="chat-send"
            onClick={() => void submitMessage()}
            disabled={sending || cooldown > 0 || !draft.trim()}
          >
            {cooldown > 0 ? `${cooldown}s` : "SEND"}
          </button>
        </div>
      ) : (
        <button className="chat-connect" onClick={onConnect}>
          CONNECT WALLET TO CHAT
        </button>
      )}
    </aside>
  );
}
